/**
 * Canvas-based PDF viewer powered by pdfjs-dist.
 *
 * Why not an <iframe>? Browsers rasterize the embedded PDF at its native size
 * and any CSS `transform: scale()` then upscales those pixels, producing a
 * fuzzy zoom. Re-rendering each page to a <canvas> at `zoom * devicePixelRatio`
 * gives crisp output at any zoom level.
 */

import { useEffect, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';
// Use the legacy ESM worker so it works through Vite's classic worker pipeline
// without extra config. ?url returns the bundled asset URL.
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

interface Props {
  /** Object URL or Blob URL for the PDF. */
  src: string;
  /** Zoom factor; 1 = fit-width. Re-rendering happens on every change. */
  zoom: number;
  /** Optional cap on pages to render. Page count is announced via onPages. */
  maxPages?: number;
  onPages?: (count: number) => void;
}

export function PdfCanvas({ src, zoom, maxPages = 200, onPages }: Props) {
  // The canvases live inside this div, which React never renders children for.
  // That way pdf.js's imperative DOM manipulation can't fight React's reconciler.
  const canvasHostRef = useRef<HTMLDivElement | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let docTask: ReturnType<typeof pdfjs.getDocument> | null = null;
    let pdf: pdfjs.PDFDocumentProxy | null = null;
    const renderTasks: pdfjs.RenderTask[] = [];

    async function render() {
      const host = canvasHostRef.current;
      if (!host) return;
      setError(null);
      try {
        docTask = pdfjs.getDocument({ url: src });
        pdf = await docTask.promise;
        if (cancelled) return;
        const count = Math.min(pdf.numPages, maxPages);
        setPageCount(count);
        onPages?.(pdf.numPages);

        // Clear any previous canvases (React doesn't render children here).
        while (host.firstChild) host.removeChild(host.firstChild);

        const containerW = host.clientWidth || 720;
        const dpr = window.devicePixelRatio || 1;

        for (let i = 1; i <= count; i += 1) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const baseViewport = page.getViewport({ scale: 1 });
          const fitScale = containerW / baseViewport.width;
          const cssScale = fitScale * zoom;
          const viewport = page.getViewport({ scale: cssScale * dpr });

          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = `${Math.floor(viewport.width / dpr)}px`;
          canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;
          canvas.className = 'pdf-page';
          host.appendChild(canvas);

          const ctx = canvas.getContext('2d', { alpha: false });
          if (!ctx) continue;
          const task = page.render({ canvasContext: ctx, viewport });
          renderTasks.push(task);
          try {
            await task.promise;
          } catch (e) {
            const msg = (e as Error)?.message ?? '';
            if (!msg.includes('cancelled')) throw e;
          }
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to render PDF');
      }
    }

    render();
    return () => {
      cancelled = true;
      renderTasks.forEach((t) => t.cancel());
      try { docTask?.destroy(); } catch { /* ignore */ }
      try { pdf?.destroy(); } catch { /* ignore */ }
      const host = canvasHostRef.current;
      if (host) {
        while (host.firstChild) host.removeChild(host.firstChild);
      }
    };
  }, [src, zoom, maxPages, onPages]);

  return (
    <div className="pdf-canvas-wrap">
      {error && <div className="muted" style={{ padding: 24 }}>{error}</div>}
      {!error && pageCount === 0 && (
        <div className="muted" style={{ padding: 24 }}>Rendering PDF…</div>
      )}
      {/* React never renders children inside the canvas host; pdf.js owns it. */}
      <div className="pdf-canvas-host" ref={canvasHostRef} />
    </div>
  );
}
